import React, { useState, useEffect, Suspense, lazy } from 'react';
import {
  Box,
  CssBaseline,
} from '@mui/material';
import Header from '../../components/Header/Header';
import Sidebar from '../../components/Sidebar/Sidebar';
import PageSkeleton from '../../components/PageSkeleton';
import { useResponsive } from '../../hooks/useResponsive';
import { trackScreen } from '../../services/analyticsService';

// 라우트별 코드 스플리팅 — 첫 진입 시 해당 페이지 청크만 로드.
// Dashboard는 기본 진입 화면이라 즉시 import (lazy 시 첫 화면 로딩 깜빡임 발생).
import Dashboard from '../Dashboard/Dashboard';

const RecordsManager = lazy(() => import('../../components/Records/RecordsManager'));
const RevenueAnalysis = lazy(() => import('../RevenueAnalysis/RevenueAnalysis'));
const ExpenseAnalysis = lazy(() => import('../ExpenseAnalysis/ExpenseAnalysis'));
const TaxConsultation = lazy(() => import('../TaxConsultation/TaxConsultation'));
const RepeatsPage = lazy(() => import('../RepeatsPage/RepeatsPage'));
const MonthlyAnalysis = lazy(() => import('../MonthlyAnalysis/MonthlyAnalysis'));
const ActivityHistoryPage = lazy(() =>
  import('../ActivityHistory').then((m) => ({ default: m.ActivityHistoryPage })),
);
const EmployeeManagement = lazy(() =>
  import('../../components/EmployeeManagement').then((m) => ({ default: m.EmployeeManagement })),
);
const DocumentManagement = lazy(() =>
  import('../DocumentManagement').then((m) => ({ default: m.DocumentManagement })),
);
const StoreManagement = lazy(() =>
  import('../StoreManagement').then((m) => ({ default: m.StoreManagement })),
);
const AllStoresOverview = lazy(() => import('../AllStoresOverview/AllStoresOverview'));
const MerchantManagement = lazy(() => import('../MerchantManagement/MerchantManagement'));
const MemoWallPage = lazy(() => import('../MemoWall/MemoWallPage'));
const LedgerTaxPage = lazy(() => import('../LedgerTax/LedgerTaxPage'));

// 페이지 이동(lazy 청크 로딩) 시 스피너 대신 페이지 골격 스켈레톤을 보여준다.
// 화면을 먼저 그리고 데이터는 채운다는 원칙 — Dashboard 첫 진입과 동일한 패턴.
const RouteFallback: React.FC = () => <PageSkeleton />;

const MainPage: React.FC = () => {
  
  const { isMobile, isTablet } = useResponsive();
  
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [currentView, setCurrentView] = useState('dashboard');
  // 사이드바 활성 메뉴 하이라이트용 — 메뉴 항목의 path 기준
  const [currentPath, setCurrentPath] = useState('/');
  // 인사관리 진입 시 활성화할 탭 — 문서관리 '인사관리에서 만들기' 연동용
  const [employeeInitialTab, setEmployeeInitialTab] = useState<'calendar' | 'records'>('calendar');

  // 화면 크기 변경 시 사이드바 상태 조정
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    } else if (isTablet) {
      setSidebarOpen(false);
    } else {
      setSidebarOpen(true);
    }
  }, [isMobile, isTablet]);

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleSidebarClose = () => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  // 첫 진입(대시보드) 추적 — 이후 화면 전환은 handleMenuClick 에서 기록
  useEffect(() => {
    trackScreen('/');
  }, []);

  const handleMenuClick = (path: string) => {
    setCurrentPath(path);
    trackScreen(path);

    // 경로에 따라 보여줄 뷰 설정
    if (path === '/') {
      setCurrentView('dashboard');
    } else if (path === '/accounts') {
      setCurrentView('analytics'); // 가계부 버튼을 누르면 거래내역 페이지로 이동
    } else if (path === '/ledger-tax') {
      setCurrentView('ledgerTax');
    } else if (path === '/memo-wall') {
      setCurrentView('memoWall');
    } else if (path === '/analytics') {
      setCurrentView('analytics');
    } else if (path === '/analytics/revenue') {
      setCurrentView('revenue');
    } else if (path === '/analytics/categories') {
      setCurrentView('expense');
    } else if (path === '/analytics/tax') {
      setCurrentView('tax');
    } else if (path === '/repeats') {
      setCurrentView('repeats');
    } else if (path === '/analytics/monthly') {
      setCurrentView('monthly');
    } else if (path === '/activity-history') {
      setCurrentView('activityHistory');
    } else if (path === '/employee-management') {
      setEmployeeInitialTab('calendar'); // 메뉴로 직접 진입하면 기본 탭
      setCurrentView('employeeManagement');
    } else if (path === '/merchants') {
      setCurrentView('merchants');
    } else if (path === '/documents') {
      setCurrentView('documentManagement');
    } else if (path === '/all-stores') {
      setCurrentView('allStoresOverview');
    }
    
    // 모바일에서는 사이드바 닫기
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  // 현재 뷰에 따른 콘텐츠 렌더링
  const renderContent = () => {
    
    switch (currentView) {
      case 'dashboard':
        return <Dashboard />;
      case 'memoWall':
        return <MemoWallPage />;
      case 'ledgerTax':
        return <LedgerTaxPage />;
      case 'analytics':
        return (
          <RecordsManager
            onNavigateToRepeats={() => {
              setCurrentView('repeats');
              setCurrentPath('/repeats');
            }}
            onNavigateToLedgerTax={() => {
              setCurrentView('ledgerTax');
              setCurrentPath('/ledger-tax');
            }}
          />
        );
      case 'revenue':
        return <RevenueAnalysis />;
      case 'expense':
        return <ExpenseAnalysis />;
      case 'tax':
        return <TaxConsultation />;
      case 'repeats':
        return <RepeatsPage />;
      case 'monthly':
        return <MonthlyAnalysis />;
      case 'activityHistory':
        return <ActivityHistoryPage />;
            case 'employeeManagement':
              return <EmployeeManagement key={employeeInitialTab} initialTab={employeeInitialTab} />;
      case 'documentManagement':
        return (
          <DocumentManagement
            onNavigateToHr={() => {
              setEmployeeInitialTab('records');
              setCurrentView('employeeManagement');
              setCurrentPath('/employee-management');
            }}
          />
        );
      case 'merchants':
        return <MerchantManagement />;
      case 'storeManagement':
        return <StoreManagement />;
      case 'allStoresOverview':
        return <AllStoresOverview />;
      default:
        return <Dashboard />;
    }
  };

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      background: 'var(--c-cool-200)', // Quixotic 레퍼런스 — 깊은 회색 캔버스 위에 화이트 카드
    }}>
      <CssBaseline />
      
      {/* Header - 최상단 고정 */}
      <Header 
        onMenuClick={handleSidebarToggle}
        onStoreManagementClick={() => {
          setCurrentView('storeManagement');
          setCurrentPath('/store-management'); // 사이드바 메뉴에 없는 경로 → 하이라이트 해제
        }}
      />
      
      {/* 메인 컨테이너 - Header 아래, Sidebar와 Content를 가로로 배치 */}
      <Box sx={{ 
        display: 'flex', 
        flex: 1,
        mt: '72px', // Header 높이만큼 상단 여백
      }}>
        {/* Sidebar - 왼쪽 고정 */}
        <Sidebar
          open={sidebarOpen}
          onClose={handleSidebarClose}
          variant={isMobile ? 'temporary' : 'permanent'}
          onMenuClick={handleMenuClick}
          activePath={currentPath}
        />
        
        {/* Main Content - Sidebar 오른쪽, 남은 공간 모두 차지 */}
        <Box
          component="main"
          sx={{
            flex: 1,
            minWidth: 0, // flexbox 오버플로우 방지
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            marginLeft: { 
              xs: 0, 
              md: sidebarOpen ? '20px' : 0 // Sidebar와의 간격을 20px로 줄임
            },
            background: 'transparent', // 배경색 제거
          }}
        >
          {/* 실제 콘텐츠 — lazy 로드된 페이지 청크는 Suspense fallback 표시 */}
          <Box sx={{
            p: { xs: 2, sm: 3, md: 4 },
            height: '100%',
          }}>
            <Suspense fallback={<RouteFallback />}>{renderContent()}</Suspense>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default MainPage;
